import React, { useCallback } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Tooltip,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
} from '@mui/material';
import {
  FormatBold as BoldIcon,
  FormatItalic as ItalicIcon,
  StrikethroughS as StrikeIcon,
  FormatListBulleted as BulletListIcon,
  FormatListNumbered as OrderedListIcon,
  FormatQuote as QuoteIcon,
  Code as CodeIcon,
  HorizontalRule as HorizontalRuleIcon,
  Undo as UndoIcon,
  Redo as RedoIcon,
  Title as HeadingIcon,
} from '@mui/icons-material';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';

import { Widget } from '../../types/canvas.types';

interface PageLayoutViewProps {
  widgets: Widget[];
  isLoading: boolean;
  content?: any;
  onContentChange?: (content: any) => void;
  renderWidget?: (widget: Widget) => React.ReactNode;
}

const MenuBar: React.FC<{ editor: any }> = ({ editor }) => {
  if (!editor) return null;

  return (
    <Paper
      variant="outlined"
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 0.5,
        p: 0.5,
        mb: 2,
        position: 'sticky',
        top: 0,
        zIndex: 1,
        bgcolor: 'background.paper',
      }}
    >
      <Tooltip title="Bold">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleBold().run()}
          color={editor.isActive('bold') ? 'primary' : 'default'}
        >
          <BoldIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Italic">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          color={editor.isActive('italic') ? 'primary' : 'default'}
        >
          <ItalicIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Strikethrough">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleStrike().run()}
          color={editor.isActive('strike') ? 'primary' : 'default'}
        >
          <StrikeIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Inline Code">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleCode().run()}
          color={editor.isActive('code') ? 'primary' : 'default'}
        >
          <CodeIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      <Tooltip title="Heading 1">
        <IconButton
          size="small"
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 1 }).run()
          }
          color={
            editor.isActive('heading', { level: 1 }) ? 'primary' : 'default'
          }
        >
          <HeadingIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Heading 2">
        <IconButton
          size="small"
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 2 }).run()
          }
          color={
            editor.isActive('heading', { level: 2 }) ? 'primary' : 'default'
          }
          sx={{ fontSize: '0.85rem', fontWeight: 'bold' }}
        >
          H2
        </IconButton>
      </Tooltip>

      <Tooltip title="Heading 3">
        <IconButton
          size="small"
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 3 }).run()
          }
          color={
            editor.isActive('heading', { level: 3 }) ? 'primary' : 'default'
          }
          sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}
        >
          H3
        </IconButton>
      </Tooltip>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      <Tooltip title="Bullet List">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          color={editor.isActive('bulletList') ? 'primary' : 'default'}
        >
          <BulletListIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Numbered List">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          color={editor.isActive('orderedList') ? 'primary' : 'default'}
        >
          <OrderedListIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Blockquote">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          color={editor.isActive('blockquote') ? 'primary' : 'default'}
        >
          <QuoteIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Horizontal Rule">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
        >
          <HorizontalRuleIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      <Tooltip title="Undo">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
        >
          <UndoIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Redo">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
        >
          <RedoIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Paper>
  );
};

export const PageLayoutView: React.FC<PageLayoutViewProps> = ({
  widgets,
  isLoading,
  content,
  onContentChange,
  renderWidget,
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          rel: 'noopener noreferrer',
          target: '_blank',
        },
      }),
      Placeholder.configure({
        placeholder:
          'Start writing your page content here. Use the toolbar above to format text.',
      }),
    ],
    content: content || '',
    onUpdate: ({ editor }) => {
      if (onContentChange) {
        onContentChange(editor.getJSON());
      }
    },
  });

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      <MenuBar editor={editor} />

      <Box
        sx={{
          '& .tiptap': {
            minHeight: '400px',
            outline: 'none',
            p: 3,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'background.paper',
            '& > * + *': { mt: 1 },
            '& h1': {
              fontSize: '2rem',
              fontWeight: 700,
              lineHeight: 1.3,
              mt: 3,
              mb: 1,
            },
            '& h2': {
              fontSize: '1.5rem',
              fontWeight: 600,
              lineHeight: 1.4,
              mt: 2.5,
              mb: 1,
            },
            '& h3': {
              fontSize: '1.25rem',
              fontWeight: 600,
              lineHeight: 1.4,
              mt: 2,
              mb: 0.5,
            },
            '& p': {
              fontSize: '1rem',
              lineHeight: 1.7,
              my: 0.5,
            },
            '& ul, & ol': {
              pl: 3,
            },
            '& li': {
              mb: 0.25,
            },
            '& blockquote': {
              borderLeft: '3px solid',
              borderColor: 'primary.main',
              pl: 2,
              ml: 0,
              color: 'text.secondary',
              fontStyle: 'italic',
            },
            '& code': {
              bgcolor: 'grey.100',
              borderRadius: 0.5,
              px: 0.5,
              py: 0.25,
              fontFamily: 'monospace',
              fontSize: '0.9em',
            },
            '& pre': {
              bgcolor: 'grey.900',
              color: 'grey.100',
              borderRadius: 1,
              p: 2,
              overflow: 'auto',
              '& code': {
                bgcolor: 'transparent',
                color: 'inherit',
                p: 0,
              },
            },
            '& hr': {
              border: 'none',
              borderTop: '2px solid',
              borderColor: 'divider',
              my: 2,
            },
            '& a': {
              color: 'primary.main',
              textDecoration: 'underline',
            },
            '& .is-empty::before': {
              content: 'attr(data-placeholder)',
              color: 'text.disabled',
              float: 'left',
              height: 0,
              pointerEvents: 'none',
            },
          },
        }}
      >
        <EditorContent editor={editor} />
      </Box>

      {/* Render inline widgets below the editor content */}
      {widgets.length > 0 && renderWidget && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Page Widgets
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {widgets.map((widget) => (
              <Box key={widget.id}>{renderWidget(widget)}</Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};
